import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { FileText, Clock, ArrowRight } from "lucide-react";
import { getToken } from "../services/auth";
import { getSubjectiveExams, getSubmissions } from "../services/subjective_exams";
import toast, { Toaster } from "react-hot-toast";

export default function StudentSubjectiveExams() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) return;
      const decoded = jwtDecode(token);
      setUser(decoded);
      
      try {
        const examsRes = await getSubjectiveExams();
        setExams(examsRes);
      } catch (err) {
        toast.error("Failed to load subjective exams");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  if (loading) return <div className="flex h-screen items-center justify-center"><div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="flex h-screen bg-gray-50">
      <Toaster position="top-right" />
            <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Descriptive Exams</h1>
            <p className="text-gray-600 mt-2">Attempt subjective writing tests assigned to you.</p>
          </div>

          {exams.length === 0 ? (
            <div className="text-center p-12 bg-white rounded-xl border border-dashed border-gray-300 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Descriptive Exams</h3>
              <p>You don't have any subjective exams assigned right now.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {exams.map(exam => (
                <div key={exam.id} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-gray-800 line-clamp-2">{exam.title}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <div className="font-semibold text-gray-900 flex items-center justify-center gap-1"><Clock size={16} /> {exam.duration_minutes}</div>
                      <div className="text-xs text-gray-500 mt-1 uppercase">Minutes</div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <div className="font-semibold text-gray-900">{exam.total_marks}</div>
                      <div className="text-xs text-gray-500 mt-1 uppercase">Marks</div>
                    </div>
                  </div>
                  {['submitted', 'evaluated'].includes(exam.student_submission_status) ? (
                    <button
                      disabled
                      className="mt-auto w-full bg-green-100 text-green-700 font-medium py-3 rounded-lg flex items-center justify-center gap-2 cursor-not-allowed"
                    >
                      Already Attempted
                    </button>
                  ) : exam.student_submission_status === 'in_progress' ? (
                    <button
                      onClick={() => navigate(`/student-subjective-exam/${exam.id}`)}
                      className="mt-auto w-full bg-amber-500 text-white font-medium py-3 rounded-lg hover:bg-amber-600 transition flex items-center justify-center gap-2"
                    >
                      Resume Exam <ArrowRight size={18} />
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/student-subjective-exam/${exam.id}`)}
                      className="mt-auto w-full bg-indigo-600 text-white font-medium py-3 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
                    >
                      Start Exam <ArrowRight size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
